import {Record, Set} from 'immutable';
import alt from 'components/Dispatcher';
import AdminFilterWordProfilePageActions from 'actions/admin/AdminFilterWordProfilePageActions';
import {Store} from '@store/@types/alt';
import {DepartmentId} from 'records/Department';
import {TeamId} from 'records/TeamId';
import NewFilterWord, {FilterWordId} from 'records/admin/topics/NewFilterWord';

/**
 *  Key that uniquely identifies a particular profile editor on the page.
 *
 *  The editor can be open either for a department or a team in a department. An instance of this class contains the
 *  department and/or team for which the editor is currently open.
 */
export class ProfileEditorKey extends Record({
  departmentId: undefined,
  teamId: undefined,
}) {
  static matches(key, departmentId, teamId) {
    if (!key) {
      return false;
    }
    return key.departmentId === departmentId && key.teamId === teamId;
  }
}

interface AdminFilterWordProfilePageStoreState {
  expandedDepartmentIds: Set<DepartmentId>;
  expandedTeamIds: Set<TeamId>;
  profileEditorKey: ProfileEditorKey | undefined;
  profileEditorDirty: boolean;
  recentMergedTopicId: FilterWordId | undefined;
  topicsSelectedForMerge: Set<NewFilterWord>;
}

/**
 * Storage for the admin filter word profile page state, as opposed to storage for data fetched
 * from the server.
 */
class AdminFilterWordProfilePageStore extends Store<AdminFilterWordProfilePageStoreState> {
  constructor() {
    super();
    this.state = {
      expandedDepartmentIds: Set(),
      expandedTeamIds: Set(),
      profileEditorKey: undefined, // At most one can be open at a time.
      profileEditorDirty: false,
      recentMergedTopicId: undefined,
      topicsSelectedForMerge: Set(),
    };
    this.bindActions(AdminFilterWordProfilePageActions);
  }

  clearRecentMergedTopicId() {
    this.setState({
      recentMergedTopicId: undefined,
    });
  }

  clearTopicsSelectedForMerge() {
    this.setState({
      topicsSelectedForMerge: Set(),
    });
  }

  collapseAllDepartments() {
    this.setState({
      expandedDepartmentIds: Set(),
    });
  }

  markProfileEditorDirty() {
    this.setState({
      profileEditorDirty: true,
    });
  }

  setRecentMergedTopicId(mergedTopicId: FilterWordId) {
    this.setState({
      recentMergedTopicId: mergedTopicId,
    });
  }

  startProfileEditor([departmentId, teamId, initiallyDirty = false]: [
    DepartmentId,
    TeamId,
    boolean?
  ]) {
    const {profileEditorKey} = this.state;
    if (!ProfileEditorKey.matches(profileEditorKey, departmentId, teamId)) {
      this.setState({
        profileEditorKey: new ProfileEditorKey({departmentId, teamId}),
        profileEditorDirty: initiallyDirty,
      });
    }
  }

  stopProfileEditor() {
    this.setState({
      profileEditorKey: undefined,
      profileEditorDirty: false,
    });
  }

  toggleDepartment(departmentId: DepartmentId) {
    const {expandedDepartmentIds} = this.state;
    const expanded = expandedDepartmentIds.has(departmentId);
    if (expanded) {
      this.setState({
        expandedDepartmentIds: expandedDepartmentIds.remove(departmentId),
      });
    } else {
      this.setState({
        expandedDepartmentIds: expandedDepartmentIds.add(departmentId),
      });
    }
  }

  toggleTeam(teamId: TeamId) {
    const {expandedTeamIds} = this.state;
    const expanded = expandedTeamIds.has(teamId);
    if (expanded) {
      this.setState({
        expandedTeamIds: expandedTeamIds.remove(teamId),
      });
    } else {
      this.setState({
        expandedTeamIds: expandedTeamIds.add(teamId),
      });
    }
  }

  toggleTopicSelectedForMerge(topic: NewFilterWord) {
    const {topicsSelectedForMerge} = this.state;
    if (topicsSelectedForMerge.has(topic)) {
      this.setState({
        topicsSelectedForMerge: topicsSelectedForMerge.remove(topic),
      });
    } else {
      this.setState({
        topicsSelectedForMerge: topicsSelectedForMerge.add(topic),
      });
    }
  }

  expandOnlyOneDepartment(departmentId: DepartmentId) {
    this.setState({
      expandedDepartmentIds: Set([departmentId]),
    });
  }
}

export default alt.createStore(AdminFilterWordProfilePageStore);
